import React, { useRef, useState, useEffect } from 'react';
import { useScrollLock } from '../hooks/useScrollLock';

// Chiude se il rilascio supera questa distanza (px) OPPURE questa velocità (px/ms).
const CLOSE_DISTANCE = 100;
const CLOSE_VELOCITY = 0.55;

/**
 * Bottom sheet riutilizzabile con comportamento uniforme:
 *  - NON si chiude cliccando sul backdrop
 *  - si chiude con drag/swipe verso il basso (handle o pannello, se in cima)
 *  - rispetta la safe area iOS, contenuto scrollabile senza tagli
 *  - z-index sopra la BottomNav (default z-[1100])
 *
 * Props:
 *  - onClose(): chiamata a chiusura completata
 *  - zIndexClass: es. 'z-[1100]' | 'z-[1200]'
 *  - footer: nodo opzionale ancorato in basso (con padding safe-area)
 *  - closeButton: se true mostra una ✕ in alto a destra
 *  - children: contenuto scrollabile
 */
function BottomSheet({ onClose, zIndexClass = 'z-[1100]', footer = null, closeButton = false, children }) {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const scrollRef = useRef(null);
  const closingRef = useRef(false);
  const [dragY, setDragY] = useState(0);
  const [animate, setAnimate] = useState(false);

  // Blocca lo scroll della pagina dietro finché lo sheet è aperto.
  useScrollLock(true);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    let startY = null;
    let active = false;
    let current = 0;
    let prevY = 0;
    let prevT = 0;
    let velocity = 0;

    const atTop = () => {
      const s = scrollRef.current;
      return !s || s.scrollTop <= 0;
    };
    const pointY = (e) => (e.touches ? e.touches[0].clientY : e.clientY);

    const onStart = (e) => {
      if (!atTop()) return;
      startY = pointY(e);
      prevY = startY;
      prevT = e.timeStamp;
      velocity = 0;
      active = true;
      current = 0;
      setAnimate(false);
    };

    const onMove = (e) => {
      if (!active || startY == null) return;
      const y = pointY(e);
      const delta = y - startY;
      if (delta > 0 && atTop()) {
        if (e.cancelable) e.preventDefault();
        const dt = e.timeStamp - prevT;
        if (dt > 0) velocity = (y - prevY) / dt;
        prevY = y;
        prevT = e.timeStamp;
        current = delta;
        setDragY(delta);
      } else {
        active = false;
        current = 0;
        setDragY(0);
      }
    };

    const onEnd = () => {
      if (!active) return;
      active = false;
      startY = null;
      const shouldClose = current > CLOSE_DISTANCE || velocity > CLOSE_VELOCITY;
      setAnimate(true);
      if (shouldClose) {
        closingRef.current = true;
        setDragY(panel.getBoundingClientRect().height || 600);
      } else {
        setDragY(0);
      }
      current = 0;
    };

    panel.addEventListener('touchstart', onStart, { passive: true });
    panel.addEventListener('touchmove', onMove, { passive: false });
    panel.addEventListener('touchend', onEnd);
    panel.addEventListener('touchcancel', onEnd);
    return () => {
      panel.removeEventListener('touchstart', onStart);
      panel.removeEventListener('touchmove', onMove);
      panel.removeEventListener('touchend', onEnd);
      panel.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  // iOS/PWA hardening: intercetta touchmove/wheel sull'intero overlay e blocca
  // il movimento OVUNQUE tranne dentro il contenuto realmente scrollabile.
  // Questo ferma lo scroll/rubber-band della pagina dietro anche quando il solo
  // body-lock non basta (iOS standalone), permettendo però lo scroll interno.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return undefined;

    const allowNative = (target) => {
      const s = scrollRef.current;
      if (!s || !s.contains(target)) return false;         // fuori dal contenuto → blocca
      return s.scrollHeight > s.clientHeight;               // contenuto scrollabile → consenti
    };

    const onTouchMove = (e) => {
      if (allowNative(e.target)) return;
      if (e.cancelable) e.preventDefault();
    };
    const onWheel = (e) => {
      if (allowNative(e.target)) return;
      if (e.cancelable) e.preventDefault();
    };

    overlay.addEventListener('touchmove', onTouchMove, { passive: false });
    overlay.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      overlay.removeEventListener('touchmove', onTouchMove);
      overlay.removeEventListener('wheel', onWheel);
    };
  }, []);

  const handleTransitionEnd = () => {
    if (closingRef.current) onClose();
    else setAnimate(false);
  };

  return (
    <div
      ref={overlayRef}
      // Nessun onClick sul backdrop → tap fuori NON chiude.
      // Lo scroll della pagina dietro è bloccato via listener touchmove/wheel
      // non-passivi (preventDefault) su questo overlay + body-lock.
      className={`fixed inset-x-0 top-0 ${zIndexClass} h-screen flex items-end justify-center bg-black/50 animate-fade-in`}
      style={{ height: '100dvh' }}
    >
      <div
        ref={panelRef}
        className="w-full max-w-lg bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[90vh]"
        style={{
          maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 16px)',
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: animate ? 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          touchAction: 'pan-y',
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* Drag handle (funzionante) + eventuale ✕ */}
        <div className="relative shrink-0">
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
          </div>
          {closeButton && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="absolute top-2 right-3 w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-base active:bg-gray-200"
            >
              ✕
            </button>
          )}
        </div>

        {/* Contenuto scrollabile (overscroll-contain: niente chaining ai bordi) */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>

        {/* Footer ancorato con safe-area reale */}
        {footer && (
          <div
            className="px-6 pt-3 shrink-0 bg-white border-t border-gray-100 flex flex-col gap-2"
            style={{ paddingBottom: 'max(24px, calc(env(safe-area-inset-bottom, 0px) + 24px))' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default BottomSheet;
